import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {Button, Form} from "react-bootstrap";
import {useMediaQuery} from "react-responsive";
import {useNavigate} from "react-router-dom";
import {api} from "../../utils/axios";
import "./styles/adminLogin.css";
import {useAppDispatch} from "../../store/hooks";
import zxcvbn from "zxcvbn";
import {updateSession} from "../../utils/session";
import OTPVerificationForm from "../../components/OTPVerification/OTPVerificationForm";

const AdminLogin = () => {
    const [step, setStep] = useState<'entry' | 'login' | 'otp' | 'setPassword'>('entry');
    const [userInput, setUserInput] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [inputType, setInputType] = useState<'email' | 'mobile' | null>(null);
    const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loginPassword, setLoginPassword] = useState("");
    const [error, setError] = useState("");
    const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
    const [isPending, setIsPending] = useState(false);
    const [enableLoginButton, setEnableLoginButton] = useState(false);
    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
    const isDesktopOrLaptop = useMediaQuery({ query: "(min-width: 768px)" });
    const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const detectInputType = (val: string): 'email' | 'mobile' | null => {
        const cleaned = val.replace(/[-\s]/g, "");
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleaned)) return 'email';
        if (/^\+?[1-9]\d{9,14}$/.test(cleaned)) return 'mobile';
        return null;
    };

    const handleInputChangeType = (val: string) => {
        setUserInput(val);
        setInputType(detectInputType(val));
    };

    const getPayload = () => ({
        email: inputType === 'email' ? userInput : "",
        mobile: inputType === 'mobile' ? userInput : ""
    });

    const checkUserStatus = async () => {
        setIsPending(true);
        setError("");
        try {
            const res = await api.post("/authentication/admin-user-status", getPayload());
            const { eligible, first_time_user } = res.data;
            if (eligible) {
                if (first_time_user) {
                    await handleSendOtp();
                } else {
                    setStep('login');
                }
            } else {
                await handleSendOtp(); // fallback if not eligible
            }
        } catch (err: any) {
            await handleSendOtp(); // treat error as new user flow
        } finally {
            setIsPending(false);
        }
    };

    useEffect(() => {
        setError("");
    }, [step]);

    const handleSendOtp = async () => {
        try {
            await api.post("/authentication/authentication-admin", getPayload());
            setStep("otp");
        } catch (err: any) {
            setError(err?.response?.data?.detail || "Failed to send OTP");
        }
    };

    const handleVerifyOtp = async () => {
        setIsVerifyingOtp(true);
        setError("");
        try {
            await api.post("/authentication/verify-admin", {
                ...getPayload(),
                otp: code.join("")
            });
            setStep("setPassword");
        } catch {
            setError("Invalid OTP");
        } finally {
         setIsVerifyingOtp(false);
     }
    };

    const handleSetPassword = async () => {
        if (password !== confirmPassword) return setError("Passwords do not match");
        setIsSubmittingPassword(true);
        setError("");
        try {
            await api.post("/authentication/admin-set-password", { password });
            await updateSession(dispatch);
            navigate("/admin-dashboard");
        } catch (error: any) {
            const message = error?.response?.data?.detail;
            setError(message || "Failed to set password. Please try again.");
        } finally {
            setIsSubmittingPassword(false);
        }
    };

    const handleLogin = async () => {
        try {
            await api.post("/authentication/login-admin", {
                ...getPayload(),
                password: loginPassword
            });
            await updateSession(dispatch);
            navigate("/admin-dashboard");
        } catch (err: any) {
            setError(err?.response?.data?.detail || "Invalid credentials");
        }
    };

    useEffect(() => {
        setEnableLoginButton(code.every(d => d.trim() !== ""));
    }, [code]);

    const handleOtpInputChange = (index: number, value: string) => {
        setError("");
        if (/^\d?$/.test(value)) {
            const newCode = [...code];
            newCode[index] = value;
            setCode(newCode);
            if (value && index < 5) inputRefs.current[index + 1]?.focus();
        }
    };

    const renderForm = () => {
        if (step === 'entry') {
            return (
                <div className="adminLogin-text-center">
                    <div className="adminLogin-main-title">Admin Portal</div>
                    <div className="adminLogin-sub-text">
                        Welcome! Enter your email or phone to get started.
                    </div>
                    <Form className="adminLogin-form" onSubmit={(e) => { e.preventDefault(); checkUserStatus(); }}>
                        <Form.Control
                            type="text"
                            placeholder="Email or Mobile"
                            className="adminLogin-input"
                            value={userInput}
                            onChange={(e) => handleInputChangeType(e.target.value)}
                        />
                        {error && <div className="text-danger">{error}</div>}
                        <Button className="adminLogin-button" type="submit" disabled={isPending}>
                            Continue
                        </Button>
                    </Form>
                </div>
            );
        }

        if (step === 'login') {
            return (
                <div className="adminLogin-text-center">
                    <div className="adminLogin-main-title">Welcome Back</div>
                    <Form className="adminLogin-form" onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
                        <Form.Control
                            type="password"
                            placeholder="Enter Password"
                            className="adminLogin-input"
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                        />
                        <div
                            className="adminLogin-forgot"
                            onClick={() => handleSendOtp()}
                        >Forgot Password?</div>
                        {error && <div className="text-danger">{error}</div>}
                        <Button className="adminLogin-button" type="submit">
                            Login
                        </Button>
                    </Form>
                </div>
            );
        }

        if (step === 'otp') {
            return (
                <OTPVerificationForm
                    code={code}
                    setCode={setCode}
                    userInput={userInput}
                    error={error}
                    isInvalid={error === "Invalid OTP"}
                    isNewOtp={false}
                    enableSubmit={enableLoginButton}
                    onBack={() => setStep("entry")}
                    onChange={handleOtpInputChange}
                    onPaste={(index, e) => {
                        e.preventDefault();
                        const pasted = e.clipboardData.getData("text").slice(0, 6);
                        if (/^\d{6}$/.test(pasted)) {
                            const newCode = [...code];
                            pasted.split("").forEach((d, i) => {
                                if (index + i < 6) newCode[index + i] = d;
                            });
                            setCode(newCode);
                        }
                    }}
                    onSubmit={handleVerifyOtp}
                    onResend={handleSendOtp}
                    isSubmitting={isVerifyingOtp}
                />
            );
        }


        if (step === 'setPassword') {
            const passwordStrength = zxcvbn(password);
            const strengthScore = passwordStrength.score;

            const passwordConditions = [
                {
                    label: "At least 8 characters",
                    isMet: password.length >= 8,
                },
                {
                    label: "At most 64 characters",
                    isMet: password.length <= 64,
                },
                {
                    label: "Is not a common or breached password",
                    isMet: strengthScore >= 2,
                },
                {
                    label: "Passwords match",
                    isMet: password === confirmPassword && password.length > 0,
                }
            ];

            const allConditionsMet = passwordConditions.every(c => c.isMet);

            const getStrengthLabel = (score: number) =>
                ["Very Weak", "Weak", "Fair", "Good", "Strong"][score] || "Unknown";

            const getStrengthColor = (score: number) =>
                ["#e74c3c", "#e67e22", "#f1c40f", "#2ecc71", "#27ae60"][score] || "#ccc";

            return (
                <div className="adminLogin-text-center">
                    <div className="adminLogin-main-title">Set Your Password</div>
                    <Form className="adminLogin-form" onSubmit={(e) => { e.preventDefault(); handleSetPassword(); }}>
                        <Form.Control
                            type={showPassword ? "text" : "password"}
                            placeholder="Password"
                            className="adminLogin-input"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <Form.Control
                            type={showPassword ? "text" : "password"}
                            placeholder="Confirm Password"
                            className="adminLogin-input"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />

                        <div className="text-start mb-2">
                            <Form.Check
                                type="checkbox"
                                label="Show password"
                                checked={showPassword}
                                onChange={() => setShowPassword(!showPassword)}
                            />
                        </div>

                        {password && (
                            <div className="text-start mb-3">
                                <div className="password-strength-bar-container">
                                    <div
                                        className="password-strength-bar"
                                        style={{
                                            width: `${(strengthScore + 1) * 20}%`,
                                            backgroundColor: getStrengthColor(strengthScore)
                                        }}
                                    />
                                </div>
                                <div
                                    className="password-strength-label"
                                    style={{ color: getStrengthColor(strengthScore) }}
                                >
                                    Strength: {getStrengthLabel(strengthScore)}
                                </div>
                                {passwordStrength.feedback.warning && (
                                    <div className="password-warning">
                                        {passwordStrength.feedback.warning}
                                    </div>
                                )}
                            </div>
                        )}

                        <ul className="password-condition-list">
                            {passwordConditions.map((cond, i) => (
                                <li key={i} className={cond.isMet ? "valid" : "invalid"}>
                                    {cond.label}
                                </li>
                            ))}
                        </ul>

                        {error && <div className="text-danger">{error}</div>}

                        <Button
                            className="adminLogin-button"
                            type="submit"
                            disabled={!allConditionsMet || isSubmittingPassword}
                        >
                            {isSubmittingPassword ? "Please wait..." : "Continue"}
                        </Button>
                    </Form>
                </div>
            );
        }
    };

    return <div className="d-flex justify-content-center flex-column">{renderForm()}</div>;
};

export default AdminLogin;
